import { templateMyCompanyPanel } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { EditCompanyPanelView } from '../view/EditCompanyPanelView';
import { EditCompanyPanelWebComponent } from '../component/EditCompanyPanelWebComponent';
import { EditCompanyPanelRepository } from '../repository/EditCompanyPanelRepository';
import { CompanyData } from '../../../types/company-data';
import { EditCompanyPanelEvents } from '../view/EditCompanyPanelEnums';
import { GzTabChangeEvent } from '../../../../ui/gzTabs/gzTabGroup/events/GzTabChangeEvent';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { GzTabGroupWebComponent } from '../../../../ui/gzTabs/gzTabGroup';
import { EditCompanyPanelTabComponents, TabIndex } from '../view/EditCompanyPanelTypes';
import { CompanyDetailsTabEvents } from '../../../common/companyDetailsTab/types/CompanyDetailsTabEvents';
import { OnCompanyPanelClose } from '../../../common/events/CompanyPanelEvent';
import { OnCompanyNameUpdates } from '../../../../companies/companiesGrid/events/events';
import { MspInProductTrialEvents } from '../../../common/mspInProductTrialsTab/types/msp-in-product-trials-types';
import { GzTabGroupEvents } from '../../../../ui/gzTabs/gzTabGroup/types/types';
import { LicensingTabEvents } from '../../../common/licensingTab/LicensingEnums';

export class EditCompanyPanelController extends BaseWebComponentController {
  public component: EditCompanyPanelWebComponent;
  public view: EditCompanyPanelView;
  public viewConstructor: typeof EditCompanyPanelView;
  public dataRepository: EditCompanyPanelRepository;
  public dataRepositoryConstructor: typeof EditCompanyPanelRepository;

  constructor(component: EditCompanyPanelWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: EditCompanyPanelView,
      viewTemplate: templateMyCompanyPanel,
      dataRepositoryConstructor: EditCompanyPanelRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.companyDetails.$companyDetailsForm,
        event: CompanyDetailsTabEvents.Cancel,
        callback: this.emitCloseCompanyWindowEvent,
      },
      {
        element: this.view.dom.companyDetails.$companyDetailsForm,
        event: CompanyDetailsTabEvents.AfterSave,
        callback: this.onSaveCompanyDetails,
      },
      {
        element: this.view.dom.$licensingGzTab,
        event: LicensingTabEvents.LicensingTabRefresh,
        callback: this.handleLicensingTabRefreshed,
      },
      {
        element: this.view.dom.$authentication,
        event: EditCompanyPanelEvents.AuthenticationTabOperationInProgress,
        callback: this.handleOperationInProgressEvent.bind(this),
      },
      {
        element: this.view.dom.$licensing,
        event: EditCompanyPanelEvents.LicensingTabOperationInProgress,
        callback: this.handleOperationInProgressEvent.bind(this),
      },
      {
        element: this.view.dom.$tabGroup,
        event: GzTabGroupEvents.TAB_CHANGE,
        callback: this.onTabChange.bind(this),
      },
      {
        element: this.view.dom.$mspInProductTrials,
        event: MspInProductTrialEvents.TrialStatusModified,
        callback: this.handleMspInProductTrialChange.bind(this),
      },
    ];
  }

  async getDataForView(): Promise<{ company: CompanyData }> {
    const company = await this.dataRepository.getManagedCompanyById(this.component.editedCompanyId);

    this.component.dispatchEvent(new OnCompanyNameUpdates(company.name));

    return {
      company: company,
    };
  }

  private async onSaveCompanyDetails(): Promise<void> {
    const updatedCompany = await this.dataRepository.getManagedCompanyById(this.component.editedCompanyId);
    this.component.refreshCompanyDetailsTab(updatedCompany);

    this.view.changeMspInProductTrialsTabVisibility(updatedCompany);
    this.component.refreshCompanyMspInProductTrialsTab(updatedCompany);

    this.view.dom.$licensing.propertiesSet = false;
    this.view.dom.$licensing.initialCompanyData = updatedCompany;
    this.view.dom.$licensing.propertiesSet = true;

    this.component.dispatchEvent(new OnCompanyNameUpdates(updatedCompany.name));
  }

  private emitCloseCompanyWindowEvent() {
    this.component.dispatchEvent(new OnCompanyPanelClose());
  }

  private async handleLicensingTabRefreshed(event: WebComponentEvent) {
    const company = event.detail.updatedCompany;
    this.view.changeMspInProductTrialsTabVisibility(company);
    this.component.refreshCompanyDetailsTab(company);
    this.component.refreshCompanyMspInProductTrialsTab(company);
  }

  private async handleMspInProductTrialChange(event: WebComponentEvent) {
    const updatedCompany = await this.dataRepository.getManagedCompanyById(this.component.editedCompanyId);
    await this.view.dom.companyDetails.$companyDetailsForm.detailsForm.setCompanyData(updatedCompany);

    this.view.dom.$licensing.propertiesSet = false;
    this.view.dom.$licensing.initialCompanyData = updatedCompany;
    this.view.dom.$licensing.propertiesSet = true;
    this.component.updateCompanyInfoForMspProductTrials(updatedCompany);
    this.view.dom.$mspInProductTrials.setTrialFeatures(
      updatedCompany.specifics.features,
      updatedCompany.specifics.mspInProductTrials
    );

    this.view.dom.$mspInProductTrials.refreshTab();

    this.view.dom.$mspInProductTrials.refreshLandingPage();
  }

  private onTabChange(event: GzTabChangeEvent) {
    const tabGroup = this.view.dom.$tabGroup;
    const indexToComponentMapping = this.view.getIndexToTabComponentMapping();

    // Prevent case for checking previous tab when first tab is active
    if (event.previousTab < 0) {
      return;
    }

    if (Object.keys(indexToComponentMapping).length === 0) {
      return;
    }

    const previousTab = indexToComponentMapping[event.previousTab].component as EditCompanyPanelTabComponents;
    const currentTab = indexToComponentMapping[event.currentTab].component as EditCompanyPanelTabComponents;
    const previousTabTitle = indexToComponentMapping[event.previousTab].title;

    analytics.trackPageView({
      parentName: 'companies',
      name: this.translateTabIndex(event.currentTab),
    });

    if (previousTab.hasUnsavedChanges) {
      this.handleHasChangesWarningDialog(event.previousTab, previousTabTitle, tabGroup, previousTab, currentTab);
    }
  }

  private handleHasChangesWarningDialog(
    targetTabIndex: number,
    targetTabTitle: string,
    gzTabGroup: GzTabGroupWebComponent,
    previousTabWebComponent: EditCompanyPanelTabComponents,
    currentTabWebComponent: EditCompanyPanelTabComponents
  ) {
    currentTabWebComponent.setViewLoadingState();
    const windowBodyHtml = `
      <p>
        ${this.dependencies.translateFn(`Changes you have made in tab ${targetTabTitle} will be lost.`)}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Discard')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Go back to changes')}
      </gz-button>`;

    const windowTitle = this.dependencies.translateFn('Discard changes?');

    const confirmCallback = () => {
      previousTabWebComponent.discardFormChanges();
      currentTabWebComponent.removeViewLoadingState();
    };
    const cancelCallback = () => {
      gzTabGroup.selectTab(targetTabIndex);
      currentTabWebComponent.removeViewLoadingState();
    };

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      cancelCallback,
      confirmCallback
    );
  }

  private handleOperationInProgressEvent(event: WebComponentEvent) {
    const isOperationInProgress = event.detail.isOperationInProgress;
    this.handleOperationInProgress(isOperationInProgress);
  }

  private handleOperationInProgress(isOperationInProgress: boolean) {
    this.view.dom?.$tabGroup?.setDisabledStateForUnselectedTabs(isOperationInProgress);
  }

  public selectTab(tabId: TabIndex) {
    analytics.trackPageView({
      parentName: 'companies',
      name: this.translateTabIndex(tabId),
    });
    this.view.dom.$tabGroup.selectTab(tabId);
  }

  private translateTabIndex(tabId: TabIndex): string {
    switch (tabId) {
      case TabIndex.DETAILS:
        return 'company details';
      case TabIndex.AUTHENTICATION:
        return 'authentication';
      case TabIndex.LICENSING:
        return 'licensing';
      case TabIndex.MSP_IN_PRODUCT_TRIALS:
        return 'product trial hub';
      default:
        return 'N/A';
    }
  }
}
